class HAHAccessibility {

  /** 
   * Defines if should log in the console the current element selected by tab key.
   * Should be `false` if on production.
   */
  static LOG_ACTIVE_ELEMENT: boolean = false

  /**
   * Defines if should log debug informations when calling `trapFocus` function.
   * Should be `false` if on production.
   */
  static DEBUG_TRAP_FOCUS: boolean = false

  /**
   * Defines the Fancybox version. Can be `2` or `4`.
   * This will change the `init` method behavior.
   */
  static FANCYBOX_VERSION: number = 4

  static selectors = {
    mainNavList: '#main-nav',
    navList: '#side-nav',
    tabLists: '[role=tablist]',
    focusableElements: 'input[type="text"]:not([disabled]), ' // inputs of type "text" that are not disabled
      + 'input[type="radio"]:not([disabled]), ' // inputs of type "radio" that are not disabled
      + 'input[type="checkbox"]:not([disabled]), ' // inputs of type "checkbox" that are not disabled
      + 'input[type="email"]:not([disabled]), ' // inputs of type "email" that are not disabled
      + 'input[type="button"]:not([disabled]), ' // inputs of type "button" that are not disabled
      + 'input[type="search"]:not([disabled]), ' // inputs of type "search" that are not disabled
      + 'input[type="number"]:not([disabled]), ' // inputs of type "search" that are not disabled
      + 'textarea:not([disabled]), ' // textareas that are not disabled
      + 'select:not([disabled]), ' // selects that are not disabled
      + 'a[href]:not([disabled]), ' // links that has "href" attribute and are not disabled 
      + 'button:not([disabled]), ' // buttons that are not disabled
      + '[data-focusable="true"]', // buttons that are not disabled
    navigationBar: {
      menuItems: '.dropdown',
      linkMenuItems: '.dropdownLink',
      subMenuItems: `.submenuContainer`,
      subMenuItem: '[role="menuitem"]'
    },
    tabsManual: {
      tabs: '[role=tab]'
    },
    radioGroup: {
      radioItems: '[role=radio]'
    }
  }

  /** Trap focus terms and conditions LB */
  static showMattressTermPopover(): void {
    try	{
      document.getElementById('mattressTermPopover').style.visibility='visible';
      document.getElementById('mattressTermPopover').style.display='';
      HAHAccessibility.trapFocus({
        container: '#mattressTermPopover',
        closeElement: '#closeMattressTermPopover'
      })
    }
    catch(e){
      alert('error');
    }
  }

  /** Trap the tab navigation focus */
  static trapFocus({
    container,
    closeElement,
    additionalCloseElements,
    tablistUpdateElements,
    triggerHTMLElement,
    firstElementToFocus,
    unbindEvents
  }: {

    /**
     * The modal / container where the focus should be trapped.
     */
    container: string,

    /**
     * The element that `closeTrapFocus` function will `click` to close the modal.
     */
    closeElement: string,

    /**
     * Elements inside the container that, when `clicked` or pressed `Enter`,
     * will call the `closeTrapFocus` function.
     */
    additionalCloseElements?: string[],

    /**
     * Elements inside the container that, when `clicked` or pressed `Enter`,
     * will restore the focusable elements inside the container.
     */
    tablistUpdateElements?: string[],

    /**
     * The element that opened the modal / the element where to focus should go
     * after closing the modal.
     */
    triggerHTMLElement?: HTMLElement,

    /**
     * The element that should be focused after opening the modal.
     * If not defined it will be automatically defined.
     */
    firstElementToFocus?: string,

    /**
     * Unable the events to be unbinded after closing the modal.
     * Should be true for trapFocus where tablistUpdateElements is defined.
     */
    unbindEvents?: boolean,
  }): void {

    const containerHTMLElement: HTMLElement = document.querySelector(container);
    const closeHTMLElement: HTMLElement = document.querySelector(closeElement);

    let focusableElementsNL: NodeListOf<Element>
    let focusableElements: Element[]
    let firstFocusableEl: Element
    let lastFocusableEl: Element
    
    const KEYCODE_TAB = 9;

    const setFocusables = () => {
      focusableElementsNL = containerHTMLElement.querySelectorAll(HAHAccessibility.selectors.focusableElements);
      focusableElements = Array.from(focusableElementsNL).filter((el: Element) => (el as HTMLElement).tabIndex !== -1)
      firstFocusableEl = focusableElements[0]
      lastFocusableEl = focusableElements[focusableElements.length - 1];

      bindEventsToFocusableElements();
    }

    const eventHandler = (e: KeyboardEvent) => {
      e.stopPropagation();
      const isTabPressed = (e.key === 'Tab' || e.keyCode === KEYCODE_TAB);
      const isEscapePressed = e.key === 'Escape'
      
      if (!isTabPressed && !isEscapePressed) {
        return;
      }
      
      // If pressed tab
      if (isTabPressed) {

        // If pressed shift + tab
        if (e.shiftKey) {
          
          // If the active element is the first focusable element,
          // focus in the last focusable element.
          if (document.activeElement === firstFocusableEl) {
            (lastFocusableEl as HTMLElement).focus();
            e.preventDefault();
          }
        }
        
        // If pressed only tab
        else {
          
          // If the active element is the last focusable element,
          // focus in the first focusable element.
          if (document.activeElement === lastFocusableEl) {
            (firstFocusableEl as HTMLElement).focus();
            e.preventDefault();
          }
        }
      }
      
      // If pressed escape
      if (isEscapePressed) {
        HAHAccessibility.closeTrapFocus(closeHTMLElement, triggerHTMLElement)
      }
    }
    
    const bindEventsToFocusableElements = () => {
      focusableElements.forEach((element) => {
        element.addEventListener('keydown', eventHandler)
      });
    }
    
    const unbindEventsToFocusableElements = () => {
      if (unbindEvents) {
        focusableElements.forEach((element) => {
          element.removeEventListener('keydown', eventHandler)
        });
      }
    }
    
    setFocusables();
    
    additionalCloseElements?.forEach((element: string) => {
      const altCloseHTMLElement: HTMLElement = containerHTMLElement.querySelector(element)
      altCloseHTMLElement.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          HAHAccessibility.closeTrapFocus(altCloseHTMLElement, triggerHTMLElement)
        }
      })
      altCloseHTMLElement.addEventListener('click', (e) => {
        HAHAccessibility.closeTrapFocus(altCloseHTMLElement, triggerHTMLElement)
      })
    })

    if (HAHAccessibility.DEBUG_TRAP_FOCUS) {
      console.log("focusableElements", focusableElements)
      console.log("firstFocusableEl", firstFocusableEl)
      console.log("lastFocusableEl", lastFocusableEl)
    }

    closeHTMLElement.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        unbindEventsToFocusableElements();
      }
    }, {once: true})

    closeHTMLElement.addEventListener('click', () => {
      unbindEventsToFocusableElements();
    }, {once: true})

    tablistUpdateElements?.forEach((element) => {
      const updaterElement: HTMLElement = containerHTMLElement.querySelector(element)
      if (updaterElement) {
        updaterElement.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            setFocusables();
          }
        })
        updaterElement.addEventListener('click', (e) => {
          setFocusables();
        });
      }
    });

    if (firstElementToFocus) {
      const firstElementToFocusHTMLElement: HTMLElement = containerHTMLElement.querySelector(firstElementToFocus);
      firstElementToFocusHTMLElement.focus();
    } else {
      (firstFocusableEl as HTMLElement).focus();
    }
  }

  /** Trap the tab focus inside the Fancybox 4 modal dialog */
  static trapFocusFancybox4({
    container,
    closeElement,
    additionalCloseElements,
    tablistUpdateElements,
    triggerHTMLElement,
    imageAltText
  }: {
    container: string,
    closeElement: string,
    additionalCloseElements?: string[],
    tablistUpdateElements?: string[],
    triggerHTMLElement?: HTMLElement,
    imageAltText?: string
  }): void {

    const containerHTMLElement: HTMLElement = document.querySelector(container);
    const closeHTMLElement: HTMLElement = document.querySelector(closeElement);

    let focusableElementsNL: NodeListOf<Element>
    let focusableElements: Element[]
    let firstFocusableEl: Element
    let lastFocusableEl: Element

    const setFocusables = (trigger?: string) => {

      focusableElements && unbindEventsToFocusableElements();
      focusableElements = [];
      focusableElementsNL = containerHTMLElement.querySelectorAll(HAHAccessibility.selectors.focusableElements);
      const currentSlide = containerHTMLElement.querySelector('.fancybox__slide.is-selected');
      let currentSlideIndex = Number(currentSlide.getAttribute('data-index'));

      if (trigger === "Next") {
        currentSlideIndex += 1
      } else if (trigger === "Previous") {
        currentSlideIndex -= 1
      }

      focusableElements = Array.from(focusableElementsNL).filter((el: Element, _index: number) => {
        if ((el as HTMLElement).title !== 'Close') return true
        if ((el as HTMLElement).title === 'Close') {
          const closeButtonIndex = Number((el.parentNode.parentNode as HTMLElement).getAttribute('data-index'))
          if (closeButtonIndex === currentSlideIndex) return true
        }
      })

      if (imageAltText) {
        $(`[data-index="${currentSlideIndex}"] .fancybox__image`).attr('alt', imageAltText);
      }

      firstFocusableEl = focusableElements[0];
      lastFocusableEl = focusableElements[focusableElements.length - 1];

      bindEventsToFocusableElements();
    }

    const KEYCODE_TAB = 9;

    additionalCloseElements?.forEach(element => {
      const altCloseHTMLElement: HTMLElement = document.querySelector(element)
      if (altCloseHTMLElement) {
        altCloseHTMLElement.addEventListener('keypress', (e) => {
          if (altCloseHTMLElement !== e.target) return;
          if (e.key === 'Enter') {
            HAHAccessibility.closeTrapFocus(closeHTMLElement, triggerHTMLElement)
          }
        });
        altCloseHTMLElement.addEventListener('click', (e) => {
          if (altCloseHTMLElement !== e.target) return;
          HAHAccessibility.closeTrapFocus(closeHTMLElement, triggerHTMLElement)
        });
      }
    });

    const eventHandler = (e: KeyboardEvent) => {
      let isTabPressed = (e.key === 'Tab' || e.keyCode === KEYCODE_TAB);
      let isEscapePressed = e.key === 'Escape'

      if (!isTabPressed && !isEscapePressed) {
        return;
      }

      if (isTabPressed) {
        const elementIndex = focusableElements.indexOf(document.activeElement);
        if (e.shiftKey) /* shift + tab */ {
          if (elementIndex === 0) {
            (lastFocusableEl as HTMLElement).focus();
            e.preventDefault();
          } else if (elementIndex > 0) {
            (focusableElements[elementIndex - 1] as HTMLElement).focus();
            e.preventDefault();
          } else {
            (lastFocusableEl as HTMLElement).focus();
            e.preventDefault();
          }
        } else /* tab */ {
          if (elementIndex === focusableElements.length - 1) {
            (firstFocusableEl as HTMLElement).focus();
            e.preventDefault();
          } else if (elementIndex >= 0) {
            (focusableElements[elementIndex + 1] as HTMLElement).focus();
            e.preventDefault();
          } else {
            (firstFocusableEl as HTMLElement).focus();
            e.preventDefault();
          }
        }
      }

      if (isEscapePressed) {
        HAHAccessibility.closeTrapFocus(closeHTMLElement, triggerHTMLElement)
      }
    }

    const bindEventsToFocusableElements = () => {
      focusableElements.forEach((element) => {
        element.addEventListener('keydown', eventHandler)
      });
    }

    const unbindEventsToFocusableElements = () => {
      focusableElements.forEach((element) => {
        element.removeEventListener('keydown', eventHandler)
      });
    }

    setFocusables();

    tablistUpdateElements?.forEach((element) => {
      const updaterElement: HTMLElement = containerHTMLElement.querySelector(element)
      if (updaterElement) {
        updaterElement.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            setFocusables(updaterElement.title);
          }
        })
        updaterElement.addEventListener('click', (e) => {
          setFocusables(updaterElement.title);
        });
      }
    });


    // firstFocusableEl.focus();    
  }

  /** End the trap for the focus inside the modal dialog */
  static closeTrapFocus(closeHTMLElement: HTMLElement, triggerHTMLElement?: HTMLElement): void {
    closeHTMLElement?.focus()
    closeHTMLElement?.click()
    triggerHTMLElement?.focus();
  }

  /**
   * 1) Calls `new NavigationBar` for each element found by `document.querySelectorAll('#main-nav')`
   * 2) Calls `new NavigationBar` for each element found by `document.querySelectorAll('#side-nav')`
   * 3) Calls `new TabsManual` for each element found by `document.querySelectorAll('[role=tablist]')`
   * 4) Applies accessibility tools for fancybox images
   * 5) Applies accessibility tools for the fancybox element
   * 6) Applies accessibility tools for fancybox inline subdetails
   */
  static init() {

    const logActiveElement = this.LOG_ACTIVE_ELEMENT

    window.addEventListener('load', function () {

      window.mainNavList = document.querySelectorAll(HAHAccessibility.selectors.mainNavList);
      if (window.mainNavList) {
        for (let i = 0; i < window.mainNavList.length; i++) {
          new NavigationBar(window.mainNavList[i]);
        }
      }

      window.navList = document.querySelectorAll(HAHAccessibility.selectors.navList);
      if (window.navList) {
        for (let i = 0; i < window.navList.length; i++) {
          new NavigationBar(window.navList[i]);
        }
      }

      window.tablists = document.querySelectorAll(HAHAccessibility.selectors.tabLists);
      if (window.tablists) {
        for (let i = 0; i < window.tablists.length; i++) {
          new TabsManual(window.tablists[i]);
        }
      }

      switch (HAHAccessibility.FANCYBOX_VERSION) {
        case 2: HAHAccessibility.initADAForFancybox2(); break;
        case 4: HAHAccessibility.initADAForFancybox4(); break;
      }

      if (logActiveElement) {
        document.addEventListener('keyup', () => console.log(document.activeElement))
      }

      console.log('Accessibility tools for ADA AAA compliance successfully initialized.')
    });
  }

  private static initADAForFancybox2() {

    $("[id^='fancyboxLBImage']").on('click', function () {
      window.triggerElement = this;
    })

    $('#addOnFancyboxLBImage').on('click', function () {
      window.triggerElement = this;
    });

    ($('.fancybox') as any).fancybox({
      afterShow: function () {
        if (window.triggerElement.getAttribute("id") !== 'addOnFancyboxLBImage') {
          const altDialogText = $(window.triggerElement).children()[0].getAttribute("alt")
          $('.fancybox-overlay .fancybox-wrap').attr("aria-label", `${altDialogText} image`)
        }
        $('.fancybox-overlay .fancybox-wrap').attr("aria-modal", "true")
        HAHAccessibility.trapFocus({
          container: '.fancybox-overlay',
          closeElement: '.fancybox-close',
          additionalCloseElements: [],
          tablistUpdateElements: [
            '.fancybox-prev',
            '.fancybox-next'
          ],
          triggerHTMLElement: window.triggerElement,
          unbindEvents: true
        });
      },
      afterClose: function () {
        window.triggerElement.focus();
      }
    })

    // Fancybox inline sub details
    $('.sub-details').on('click', function () {
      window.triggerElement = this;
    });

    ($('.sub-details') as any).fancybox({
      afterShow: function () {
        $('.fancybox-overlay').removeAttr("aria-label").attr("aria-modal","true").attr("role","dialog").attr("aria-describedby","inline-sub-box ADA-sub-disclosure");
        $('.fancybox-overlay .fancybox-wrap').removeAttr("tabindex").removeAttr("aria-label").removeAttr("role");
        HAHAccessibility.trapFocus({
          container: '.fancybox-overlay',
          closeElement: '.fancybox-close',
          additionalCloseElements: [],
          tablistUpdateElements: [],
          triggerHTMLElement: window.triggerElement
        });
      },
      afterClose: function () {
        window.triggerElement.focus();
      }
    });

    $(".BMSM-Chart").on('click', function() {
      window.triggerElement = this;
    });

    ($('.BMSM-Chart') as any).fancybox({
      afterShow: function() {
        $('.fancybox-overlay').attr("aria-modal","true").attr("role","dialog").attr("aria-label","Buy More Save More");
        $('.fancybox-overlay .fancybox-wrap').removeAttr("aria-modal").removeAttr("aria-label").removeAttr("role");
        HAHAccessibility.trapFocus({
          container: '.fancybox-overlay',
          closeElement: '.fancybox-close',
          firstElementToFocus: '#bmsm-title'
          });
      },
      afterClose: function() {
        window.triggerElement.focus();
      }
    })
  }

  private static initADAForFancybox4() {

    $('[data-fancybox="product-image-gallery"]').on('click', function () {
      window.triggerElement = this;
    })

    $('[data-fancybox="product-addon"]').on('click', function () {
      window.triggerElement = this;
    })

    $('[data-fancybox-type="inline"]').on('click', function () {
      window.triggerElement = this;
    })

    Fancybox.bind('[data-fancybox="product-image-gallery"]', {
      once: {
        done: (fancybox: any) => {
          const altDialogText = $(window.triggerElement).children()[0].getAttribute("alt")
          const fancyboxId = fancybox.$container.getAttribute('id')
          fancybox.$container.setAttribute('aria-label', `${altDialogText} image`)
          $('.is-selected').attr('aria-label', 'Current image')
          HAHAccessibility.trapFocusFancybox4({
            container: `#${fancyboxId}`,
            closeElement: ".is-selected .carousel__button.is-close",
            additionalCloseElements: [".fancybox__slide"],
            tablistUpdateElements: [".carousel__button.is-next", ".carousel__button.is-prev"],
            triggerHTMLElement: window.triggerElement,
            imageAltText: altDialogText
          });
        }
      },
      Thumbs: false,
      Toolbar: false,
      dragToClose: false,
      infinite: false,
    })

    Fancybox.bind('[data-fancybox="product-addon"]', {
      once: {
        done: (fancybox: any) => {
          const altDialogText = $(window.triggerElement).text().toLowerCase().replace("add", "").trim()
          const newAltDialogText = `${altDialogText} image`
          const fancyboxId = fancybox.$container.getAttribute('id')
          fancybox.$container.setAttribute('aria-label', newAltDialogText)
          HAHAccessibility.trapFocusFancybox4({
            container: `#${fancyboxId}`,
            closeElement: ".is-selected .carousel__button.is-close",
            additionalCloseElements: [".fancybox__slide"],
            tablistUpdateElements: [],
            triggerHTMLElement: window.triggerElement,
            imageAltText: altDialogText
          });
        }
      },
      Thumbs: false,
      Toolbar: false,
      dragToClose: false,
      infinite: false,
    });

    Fancybox.bind('[data-fancybox-type="inline"]', {
      once: {
        done: (fancybox: any) => {
          const fancyboxId = fancybox.$container.getAttribute('id')
          fancybox.$container.setAttribute('aria-label','Buy More Save More')
          HAHAccessibility.trapFocusFancybox4({
            container: `#${fancyboxId}`,
            closeElement: ".is-selected .carousel__button.is-close",
            additionalCloseElements: [".fancybox__slide"],
            tablistUpdateElements: [],
            triggerHTMLElement: window.triggerElement
          });
        }
      },
      Thumbs: false,
      Toolbar: false,
      dragToClose: false,
      infinite: false,
    });
  }
}

/**
* 1) Receives an element representing the navigation list
* 2) Defines the `menuItems`, `linkMenuItems` and `subMenuItems`
* 3) Defines the first and last menu item
* 4) Adds an event listener for the `keydown` event for each element of `linkMenuItems`
* 5) Adds an event listener for the `keydown` event for each element of `subMenuItems`
* 6) Defines the `allNavLink`
* 7) Defines the `aria-current="page"` for one element of `allNavLink`
*/
class NavigationBar {

  navlistNode: Element
  menuItems: Element[]
  linkMenuItems: Element[]
  subMenuItems: Element[][]
  currentLocation: any
  firstMenuItem: Element
  lastMenuItem: Element
  allNavLink: HTMLLinkElement[]

  constructor(groupNode: Element) {
    this.navlistNode = groupNode;
    this.menuItems = Array.from(this.navlistNode.querySelectorAll(HAHAccessibility.selectors.navigationBar.menuItems));
    this.linkMenuItems = [];
    this.subMenuItems = [];
    this.currentLocation = window.location.href

    for (let i = 0; i < this.menuItems.length; i++) {
      this.linkMenuItems.push(this.menuItems[i].querySelector(HAHAccessibility.selectors.navigationBar.linkMenuItems))
      if (this.menuItems[i].querySelector(HAHAccessibility.selectors.navigationBar.subMenuItems)) {
        this.subMenuItems.push(
          Array.from(
            this.menuItems[i]
              .querySelector(HAHAccessibility.selectors.navigationBar.subMenuItems)
              .querySelectorAll(HAHAccessibility.selectors.navigationBar.subMenuItem)
          )
        )
      } else {
        this.subMenuItems.push([])
      }
    }

    this.firstMenuItem = this.linkMenuItems[0];
    this.lastMenuItem = this.linkMenuItems[this.linkMenuItems.length - 1];

    for (let j = 0; j < this.linkMenuItems.length; j++) {
      this.linkMenuItems[j].addEventListener('keydown', (e) => this.onKeydown.call(this, e, j, null))
    }

    for (let j = 0; j < this.linkMenuItems.length; j++) {
      for (let k = 0; k < this.subMenuItems[j].length; k++) {
        this.subMenuItems[j][k].addEventListener('keydown', (e) => this.onKeydown.call(this, e, j, k))
      }
    }

    this.allNavLink = Array.from(this.navlistNode.querySelectorAll(HAHAccessibility.selectors.navigationBar.subMenuItem))

    for (let l = 0; l < this.allNavLink.length; l++) {
      if (this.allNavLink[l].href === this.currentLocation) {
        this.allNavLink[l].setAttribute('aria-current', 'page');
      }
    }
  }

  moveFocusToLink(currentLink: Element) {
    (currentLink as HTMLElement)?.focus();
  }

  moveFocusToPreviousLink(indexMenu: any, _indexSubMenu: any) {
    if (indexMenu === 0) {
      this.moveFocusToLink(this.lastMenuItem)
    } else {
      this.moveFocusToLink(this.linkMenuItems[indexMenu - 1])
    }
  }

  moveFocusToNextLink(indexMenu: any, _indexSubMenu: any) {
    if (indexMenu === this.linkMenuItems.length - 1) {
      this.moveFocusToLink(this.firstMenuItem)
    } else {
      this.moveFocusToLink(this.linkMenuItems[indexMenu + 1])
    }
  }

  moveFocusToPreviousSublink(indexMenu: any, indexSubMenu: any) {
    const subMenuLenght = this.subMenuItems[indexMenu].length - 1
    if (indexSubMenu === null) {
      this.moveFocusToLink(this.subMenuItems[indexMenu][subMenuLenght]);
    } else {
      if (indexSubMenu === 0) {
        this.moveFocusToLink(this.subMenuItems[indexMenu][subMenuLenght]);
      } else {
        this.moveFocusToLink(this.subMenuItems[indexMenu][indexSubMenu - 1]);
      }
    }
  }

  moveFocusToNextSublink(indexMenu: any, indexSubMenu: any) {
    const subMenuLenght = this.subMenuItems[indexMenu].length - 1
    if (indexSubMenu === null) {
      this.moveFocusToLink(this.subMenuItems[indexMenu][0]);
    } else {
      if (indexSubMenu === subMenuLenght) {
        this.moveFocusToLink(this.subMenuItems[indexMenu][0]);
      } else {
        this.moveFocusToLink(this.subMenuItems[indexMenu][indexSubMenu + 1]);
      }
    }
  }

  moveFocusToFirstlink(indexMenu: any, indexSubMenu: any) {
    if (indexSubMenu === null) {
      this.moveFocusToLink(this.firstMenuItem);
    } else {
      this.moveFocusToLink(this.subMenuItems[indexMenu][0]);
    }
  }

  moveFocusToLastlink(indexMenu: any, indexSubMenu: any) {
    const subMenuLenght = this.subMenuItems[indexMenu].length - 1
    if (indexSubMenu === null) {
      this.moveFocusToLink(this.lastMenuItem);
    } else {
      this.moveFocusToLink(this.subMenuItems[indexMenu][subMenuLenght]);
    }
  }

  moveFocusTofatherlink(indexMenu: any, indexSubMenu: any) {
    if (indexSubMenu !== null) {
      this.moveFocusToLink(this.linkMenuItems[indexMenu]);
    }
  }

  isPrintableCharacter(str: string) {
    return str.length === 1 && str.match(/\S/);
  }

  setFocusByFirstCharacter(character: any, indexMenu: any, indexSubMenu: any) {

    let linksArrToSearch: any
    let startIndex: any

    if (indexSubMenu === null) {
      linksArrToSearch = this.linkMenuItems;
      startIndex = indexMenu
    } else {
      linksArrToSearch = this.subMenuItems[indexMenu];
      startIndex = indexSubMenu
    }

    for (let i = 0; i < linksArrToSearch.length - 1; i++) {
      if (startIndex === linksArrToSearch.length - 1) {
        startIndex = 0;
      } else {
        startIndex += 1;
      }
      if (linksArrToSearch[startIndex].name.toLowerCase().startsWith(character.toLowerCase())) {
        this.moveFocusToLink(linksArrToSearch[startIndex]);
        break;
      }
    }
  }

  /* EVENT HANDLERS */
  onKeydown(event: KeyboardEvent, indexMenu: number, indexSubMenu: number) {

    let flag = false;

    switch (event.key) {
      case 'ArrowLeft':
        this.moveFocusToPreviousLink(indexMenu, indexSubMenu);
        flag = true;
        break;
      case 'ArrowRight':
        this.moveFocusToNextLink(indexMenu, indexSubMenu);
        flag = true;
        break;
      case 'ArrowUp':
        this.moveFocusToPreviousSublink(indexMenu, indexSubMenu);
        flag = true;
        break;

      case 'ArrowDown':
        this.moveFocusToNextSublink(indexMenu, indexSubMenu);
        flag = true;
        break;
      case 'Home':
        this.moveFocusToFirstlink(indexMenu, indexSubMenu);
        flag = true;
        break;
      case 'End':
        this.moveFocusToLastlink(indexMenu, indexSubMenu);
        flag = true;
        break;

      case "Escape":
        this.moveFocusTofatherlink(indexMenu, indexSubMenu);
        flag = true;
        break;
      default:
        if (this.isPrintableCharacter(event.key)) {
          this.setFocusByFirstCharacter(event.key, indexMenu, indexSubMenu);
          flag = true;
        }
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
}

/**
* 1) Receives an element representing the tablist
* 2) Searches for subelements with `role="tab"`
* 3) Defines the first and last tab
* 4) Adds an event listener for the `keydown` event for each tab
*/
class TabsManual {

  tablistNode: any
  tabs: Element[]
  firstTab: Element
  lastTab: Element

  constructor(groupNode: Element) {
    this.tablistNode = groupNode;
    this.tabs = [];
    this.firstTab = null;
    this.lastTab = null;
    this.tabs = Array.from(this.tablistNode.querySelectorAll(HAHAccessibility.selectors.tabsManual.tabs));
    for (let i = 0; i < this.tabs.length; i += 1) {
      let tab = this.tabs[i];

      tab.addEventListener('keydown', this.onKeydown.bind(this));
      if (!this.firstTab) {
        this.firstTab = tab;
      }
      this.lastTab = tab;
    }
  }

  moveFocusToTab(currentTab: Element) {
    (currentTab as HTMLElement).focus();
  }

  moveFocusToPreviousTab(currentTab: Element) {
    let index: number;
    if (currentTab === this.firstTab) {
      this.moveFocusToTab(this.lastTab);
    } else {
      index = this.tabs.indexOf(currentTab);
      this.moveFocusToTab(this.tabs[index - 1]);
    }
  }

  moveFocusToNextTab(currentTab: Element) {
    let index: number;
    if (currentTab === this.lastTab) {
      this.moveFocusToTab(this.firstTab);
    } else {
      index = this.tabs.indexOf(currentTab);
      this.moveFocusToTab(this.tabs[index + 1]);
    }
  }

  /* EVENT HANDLERS */
  onKeydown(event: KeyboardEvent) {
    const tgt: HTMLElement = event.currentTarget as HTMLElement
    let flag = false
    switch (event.key) {
      case 'ArrowLeft':
        this.moveFocusToPreviousTab(tgt as HTMLElement);
        flag = true;
        break;
      case 'ArrowRight':
        this.moveFocusToNextTab(tgt as HTMLElement);
        flag = true;
        break;
      case 'Home':
        this.moveFocusToTab(this.firstTab);
        flag = true;
        break;
      case 'End':
        this.moveFocusToTab(this.lastTab);
        flag = true;
        break;
      default:
        break;
    }
    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
}

/**
* 1) Receives a list of elements
* 2) Searches for subelements with `role="radio"`
* 3) Defines the first and last radio option
* 4) Adds an event listener for the `keydown` event for each radio item
*/
class RadioGroup {

  radioOptNode: Element
  radioItems: Element[]
  firstRadioItem: Element
  lastRadioItem: Element

  constructor(groupNode: NodeListOf<Element>) {
    this.radioOptNode = groupNode[0];
    this.radioItems = Array.from(this.radioOptNode.querySelectorAll(HAHAccessibility.selectors.radioGroup.radioItems));
    this.firstRadioItem = this.radioItems[0];
    this.lastRadioItem = this.radioItems[this.radioItems.length - 1];
    for (let j = 0; j < this.radioItems.length; j++) {
      this.radioItems[j].addEventListener('keydown', (e) => this.onKeydown.call(this, e, j))
    }
  }

  moveFocusToRadio(currentRadio: Element) {
    (currentRadio as HTMLElement).focus();
    (currentRadio as HTMLElement).click();
  }

  moveFocusToPreviousRadio(indexRadio: number) {
    if (indexRadio === 0) {
      this.moveFocusToRadio(this.lastRadioItem)
    } else {
      this.moveFocusToRadio(this.radioItems[indexRadio - 1])
    }
  }

  moveFocusToNextRadio(indexRadio: number) {
    if (indexRadio === this.radioItems.length - 1) {
      this.moveFocusToRadio(this.firstRadioItem)
    } else {
      this.moveFocusToRadio(this.radioItems[indexRadio + 1])
    }
  }

  /* EVENT HANDLERS */
  onKeydown(event: KeyboardEvent, indexMenu: number) {

    let flag = false;

    switch (event.key) {
      case 'ArrowLeft':
        this.moveFocusToPreviousRadio(indexMenu);
        flag = true;
        break;
      case 'ArrowRight':
        this.moveFocusToNextRadio(indexMenu);
        flag = true;
        break;
      case 'ArrowUp':
        this.moveFocusToPreviousRadio(indexMenu);
        flag = true;
        break;

      case 'ArrowDown':
        this.moveFocusToNextRadio(indexMenu);
        flag = true;
        break;
      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
}
